import { Array as YArray } from 'yjs';
import { Graphics, Sprite, Container, Assets, Ticker, PointData } from 'pixi.js';
import { View } from './view';
import { Token } from './token';
import { Image } from './image';
import { Settings } from './settings';
import { Room } from 'src/sync/room';
import { Util } from 'src/util/util';
import { Store } from 'src/sync/store';
import { IAttendee, IImage, IRoomAsset, IToken, L } from 'src/sync/_api';
import { Layer } from './layer';
import { Laser } from './laser';
import { Graffiti, Stroke } from './graffiti';

interface GfxContainer {
    layers: { [id: string]: Container };
    tokens: Container;
    labels: Container;
    lasers: Container;
}

export class Gfx {
    public static activeLayer: Layer = { id: null, cfg: {} } as any;
    public static layers: { [id: string]: Layer; } = {};
    public static tokens: { [id: string]: Token } = {};
    public static maps: { [id: string]: Image } = {};
    public static textures: { [id: string]: any } = {};
    public static playArea = new Graphics();
    public static playGrid = new Graphics();
    public static containers: GfxContainer = { layers: {}, tokens: new Container(), labels: new Container(), lasers: new Container() }
    public static mouse = new Graphics();
    public static selection = new Graphics();
    public static lasers: { [id: string]: Laser; } = {};

    // NB: Render Order matters
    public static async initialize() {
        const textureFiles = [
            'null-ring',
            'null-ring-icon',
            'metal-ring',
            'rope-ring',
            'harsh-ring',
            'iron-ring',
            'aztec-ring',
            'star-ring',
            'leaf-ring',
            'flame-ring',
            'thin-ring',
            'segment-ring',
            'cyber-ring',
            'shield',
            'question'
        ];

        Promise.all(textureFiles.map(async name => {
            Gfx.textures[name] = await Assets.load(`./assets/textures/${name}.png`);
        }));

        View.viewport.addChild(Gfx.playArea);
        Gfx.containers.layers[L.Uploads] = View.viewport.addChild(new Container());
        Gfx.containers.layers[L.Mapping] = View.viewport.addChild(new Container());
        View.viewport.addChild(Gfx.playGrid);
        Gfx.containers.layers[L.Graffiti] = View.viewport.addChild(new Container());
        Gfx.containers.layers[L.Tokens] = View.viewport.addChild(new Container());
        Gfx.containers.layers[L.Tokens].addChild(Gfx.containers.tokens);
        Gfx.containers.layers[L.Tokens].addChild(Gfx.containers.labels);
        Gfx.containers.layers[L.Fog] = View.viewport.addChild(new Container());
        View.viewport.addChild(Gfx.mouse);
        View.viewport.addChild(Gfx.selection);
        View.viewport.addChild(Gfx.containers.lasers);
        Gfx.mouse.eventMode = 'none';
        Gfx.selection.eventMode = 'none';

        /* Register a laser pointer for each user */
        Room.attendance.watchAll(() => {
            const attendance = Room.attendance.toJSON() as { [index: string]: IAttendee };
            for (let id in attendance) {
                const laser = Room.lasers.get(id);
                if (laser == null) {
                    Room.lasers.set(id, new YArray<PointData>());
                }

                if (Gfx.lasers[id] == null) {
                    Gfx.lasers[id] = new Laser(Gfx.containers.lasers, id);
                }

                Gfx.lasers[id].color = attendance[id]?.color?.slice(1);
            }
        });

        /* Initialize and observe layers */
        Gfx.createDefaultLayers();
        Gfx.observeLayers();

        /* Observe and Initialize token sprites */
        Gfx.observeTokens();
        const tokens = Room.tokens.toJSON();
        const tokenPromises = Object.values(tokens).map(async token => {
            const asset = Room.tokenAssets.get(token.assetId) as IRoomAsset;
            const sprite = await Util.loadImageAsync(asset);
            await Gfx.addTokenSprite(token, sprite);
        });
        await Promise.all(tokenPromises);

        /* Observe fog and modify token interactivity */
        Gfx.observeFog();

        /* Observe and Initialize map sprites */
        Gfx.observeMaps();
        for (const asset of Room.imageAssets.values()) {
            const image = Room.images.get(asset.id);
            const sprite = await Util.loadImageAsync(asset);
            await Gfx.addImageSprite(image, sprite);
        }

        /* Graffiti */
        Gfx.containers.layers[L.Graffiti] = View.app.stage.addChild(Graffiti.create());

        /* Listen for laser data and insert */
        Room.lasers.observeDeep((events) => {
            for (let event of events) {
                const laserId = event.path[0]
                for (let d of event.delta) {
                    if (d.insert) {
                        const points = d.insert as Array<PointData>;
                        const timedPoints = points.map(p => ({ x: p.x, y: p.y, time: Date.now() }));
                        Gfx.lasers[laserId].data.push(...timedPoints);
                    }
                }
            }
        });

        const animateLasers = () => {
            for (let key of Object.keys(Gfx.lasers)) {
                Gfx.lasers[key].animate();
            }
        }

        Ticker.shared.add(() => animateLasers());
    }

    public static reorderChildren(gfx: Container) {
        gfx.children.sort((a, b) => a.zIndex - b.zIndex);
        for (let i = 0; i < gfx.children.length; i++) {
            gfx.children[i].zIndex = i;
        }
        gfx.sortChildren();
    }

    public static observeLayers() {
        Room.layers.watch((id) => {
            const layer = Room.layers.get(id);
            Gfx.layers[id].update(layer);
        });
    }

    // Performance A+
    public static observeFog() {
        Gfx.layers[L.Fog].deltas.observe(x => {
            for (let id in Gfx.tokens) {
                Gfx.tokens[id].updateVisibility(Gfx.tokens[id].data.visible);
            }
        });
    }

    public static observeTokens() {
        Room.tokens.observe(async (event) => {
            for (let key of event.changes.keys) {
                const id = key[0] as string;
                const action = key[1].action;
                const token = Room.tokens.get(id);

                if (action == 'add') {
                    const asset = Room.tokenAssets.get(token.assetId) as IRoomAsset;
                    const sprite = await Util.loadImageAsync(asset);
                    await Gfx.addTokenSprite(token, sprite);
                } else if (action == 'delete') {
                    Gfx.tokens[id]?.destroy();
                    delete Gfx.tokens[id];
                }
            }
        });

        Room.tokens.watch((id) => {
            if (Gfx.tokens[id] != null) {
                Gfx.tokens[id].update(Room.tokens.get(id));
                Gfx.layers[L.Tokens].reorderChildren();
            }
        });

        Room.tokenAssets.watchAll(async (assetIds) => {
            for (let id in Gfx.tokens) {
                if (Gfx.tokens[id].uncertainAsset) {
                    const found = assetIds.indexOf(Gfx.tokens[id].data.assetId) > -1;
                    if (found) {
                        // TODO: Any way we can just swap the sprites out instead??
                        // const asset = await Gfx.getSpriteAsset(Gfx.tokens[id].data.assetId);
                        // Gfx.tokens[id].sprite = asset.sprite;
                        const clone = Util.clone(Gfx.tokens[id].data);
                        clone.id = crypto.randomUUID();
                        Room.tokens.delete(id);
                        Room.tokens.set(clone.id, clone);
                    }
                }
            }
        })
    }

    public static observeMaps() {
        Room.imageAssets.observe(async (event) => {
            for (let key of event.changes.keys) {
                const id = key[0];
                const action = key[1].action;
                const asset = Room.imageAssets.get(id) as IRoomAsset;

                if (action == 'add') {
                    const sprite = await Util.loadImageAsync(asset);
                    const image = Room.images.get(asset.id);
                    await Gfx.addImageSprite(image, sprite);
                    Gfx.updateMapInteractivity();
                } else if (action == 'delete') {
                    Gfx.maps[id]?.destroy();
                    delete Gfx.maps[id];
                }
            }
        });

        Room.images.watch((id, action) => {
            if (action == 'update' && Gfx.maps[id] != null) {
                const image = Room.images.get(id);
                Gfx.maps[id].data = image;
                Gfx.maps[id].redraw();
                Gfx.maps[id].sprite.position.copyFrom(image.position);
                Gfx.maps[id].sprite.zIndex = Number.MAX_SAFE_INTEGER;
                Gfx.reorderChildren(Gfx.containers.layers[L.Uploads]);
            }
        });
    }

    public static async addTokenSprite(token: IToken, sprite: Sprite) {
        if (token.position == null) {
            Room.tokens.delete(token.id);
            return;
        }

        const container = Gfx.containers.tokens.addChild(new Container());
        const label = Gfx.containers.labels.addChild(new Container());

        try {
            container.addChild(sprite);
            Gfx.tokens[token.id] = new Token(token, sprite, container, label);
            Gfx.tokens[token.id].uncertainAsset = false;
        } catch {
            const sprite = new Sprite(Gfx.textures['question']);
            Gfx.tokens[token.id] = new Token(token, sprite, container, label);
            Gfx.tokens[token.id].uncertainAsset = true;
        }
    }

    public static async addImageSprite(image: IImage, sprite: Sprite) {
        const container = Gfx.containers.layers[L.Uploads].addChild(new Container());
        container.addChild(sprite);

        try {
            const center = Settings.worldSize / 2;
            const leftOffset = Math.round(sprite.width / 200) * 100;
            const topOffset = Math.round(sprite.height / 200) * 100;
            sprite.position.set(center - leftOffset, center - topOffset);
            Gfx.maps[image.id] = new Image(image, sprite, container);
        } catch (error) {
            const sprite = new Sprite(Gfx.textures['question']);
            Gfx.maps[image.id] = new Image(image, sprite, container);
        }
    }

    public static updateMapInteractivity() {
        const canEditMaps = Gfx.activeLayer.id == L.Uploads && Store.fullAccess.value == true;
        for (let id in Gfx.maps) {
            Gfx.maps[id].sprite.cursor = canEditMaps ? 'all-scroll' : 'inherit';
            Gfx.maps[id].sprite.eventMode = canEditMaps ? 'static' : 'none';
        }
    }

    public static updateTokenCursor(erasing: boolean, layer: L) {
        for (let id in Gfx.tokens) {
            const trashcan = erasing && layer == L.Tokens;
            Gfx.tokens[id].sprite.cursor = trashcan ? 'url(./assets/trashcan.png) 16 16, auto' : 'all-scroll';
        }
    }

    private static createDefaultLayers() {
        const defaults = {
            alpha: 1
        };

        Gfx.layers[L.Uploads] = new Layer(L.Uploads, {
            ...defaults,
            name: 'Uploads',
            events: 'static',
            extents: 'sprites',
            hotkey: 'KeyU',
            selectable: false,
        }, Room.layers.get(L.Uploads));
        Gfx.layers[L.Mapping] = new Layer(L.Mapping, {
            ...defaults,
            name: 'Mapping',
            events: 'none',
            extents: 'polygon',
            hotkey: 'KeyM',
            selectable: true,
            drawing: {
                fillColor: '0xF1ECDF',
                lineColor: '0x000000',
                lineAmplitude: 2,
                lineWidth: 8,
            }
        }, Room.layers.get(L.Mapping));
        Gfx.layers[L.Graffiti] = new Layer(L.Graffiti, {
            ...defaults,
            name: 'Graffiti',
            events: 'none',
            extents: 'polygon',
            hotkey: 'KeyG',
            selectable: false
        }, Room.layers.get(L.Graffiti));
        Gfx.layers[L.Tokens] = new Layer(L.Tokens, {
            ...defaults,
            name: 'Tokens',
            events: 'static',
            extents: 'sprites',
            hotkey: 'KeyT',
            selectable: true,
        }, Room.layers.get(L.Tokens));
        Gfx.layers[L.Fog] = new Layer(L.Fog, {
            ...defaults,
            name: 'Fog of War',
            alpha: 0.7,
            events: 'none',
            extents: 'holes',
            hotkey: 'KeyF',
            selectable: true,
            drawing: {
                fillColor: '0x333333',
                lineColor: '0x000000',
                lineAmplitude: 0,
                lineWidth: 0,
            }
        }, Room.layers.get(L.Fog));
        const uploadMask = new Graphics().rect(0, 0, View.viewport.worldWidth, View.viewport.worldHeight).fill();
        Gfx.containers.layers[L.Uploads].addChild(uploadMask);
        Gfx.containers.layers[L.Uploads].mask = uploadMask;
        const tokenMask = new Graphics().rect(0, 0, View.viewport.worldWidth, View.viewport.worldHeight).fill();
        Gfx.containers.layers[L.Tokens].addChild(tokenMask);
        Gfx.containers.layers[L.Tokens].mask = tokenMask;
    }
}
